import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Mail, XCircle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
export type PaymentFailure = 'declined' | 'cancelled';
interface PaymentResultScreenProps {
  outcome: PaymentFailure;
  /** Last 4 digits of the card that was tried. */
  cardLast4: string;
  onRetry: () => void;
  onUseAnotherCard: () => void;
  onExit: () => void;
}
const SUPPORT_EMAIL = 'support@vexu.ai';

/** Full-screen result shown over checkout when the gateway declines or the user cancels payment. */
export function PaymentResultScreen({
  outcome,
  cardLast4,
  onRetry,
  onUseAnotherCard,
  onExit
}: PaymentResultScreenProps) {
  const {
    isLight
  } = useTheme();
  const strong = isLight ? 'text-slate-900' : 'text-white';
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  const declined = outcome === 'declined';
  return <motion.div role="alertdialog" aria-modal="true" aria-labelledby="payment-result-title" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className={`absolute inset-0 z-[60] flex flex-col ${isLight ? 'bg-white' : 'bg-black'}`} data-id="element-362" data-mp="1rbh5wz5cgtiw">
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-363" data-mp="3u5fj10ymitz6">
        <motion.div initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1],
        delay: 0.05
      }} className={`grid h-16 w-16 place-items-center rounded-full ${declined ? isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/10 text-red-400' : isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-364" data-mp="36fl6kki7v7fg">
          {declined ? <XCircle className="h-8 w-8" data-id="element-365" data-mp="uopktnvmxtvk" /> : <RotateCcw className="h-7 w-7" data-id="element-366" data-mp="prhbmcsgt06g" />}
        </motion.div>
        <h2 id="payment-result-title" className={`mt-6 text-2xl font-bold tracking-tight ${strong}`} data-id="element-367" data-mp="pqxxl75xqvxp">
          {declined ? 'Payment declined' : 'Payment cancelled'}
        </h2>
        <p className={`mt-2 max-w-[18rem] text-sm leading-relaxed ${muted}`} data-id="element-368" data-mp="3ohh2h6zxzpyo">
          {declined ? `Your bank declined the card ending in ${cardLast4}. You haven't been charged.` : "The payment wasn't completed, so you haven't been charged. Your plan choice is saved."}
        </p>

        {declined && <div className={`mt-6 w-full rounded-2xl p-4 text-left ${isLight ? 'bg-slate-50' : 'bg-white/[0.04]'}`} data-id="element-369" data-mp="30quyprwz0ruh">
            <p className={`text-xs font-bold ${strong}`} data-id="element-370" data-mp="39tmchydbjzpa">Common reasons</p>
            <ul className={`mt-2 space-y-1.5 text-xs leading-relaxed ${muted}`} data-id="element-371" data-mp="39sb9w1vm0xzr">
              <li data-id="element-372" data-mp="3el4b4frvdo73">· Card details don't match your bank's records</li>
              <li data-id="element-373" data-mp="2h5i24zjsyyg9">· Not enough available balance</li>
              <li data-id="element-374" data-mp="2h2vz9d7ux74d">· Your bank blocked an online or international payment</li>
            </ul>
          </div>}
      </div>

      <div className="space-y-2 px-6 pb-8" data-id="element-375" data-mp="2ekaejz03xd6d">
        {declined ? <>
            <Button fullWidth onClick={onUseAnotherCard} className="flex items-center justify-center gap-2 py-3.5" data-id="element-376" data-mp="2ei68pbine43g">
              <CreditCard className="h-4 w-4" data-id="element-377" data-mp="24m2zjlpdp570" />
              Use another card
            </Button>
            <Button fullWidth variant="secondary" onClick={onRetry} data-id="element-378" data-mp="20l17botvptej">
              Try this card again
            </Button>
          </> : <>
            <Button fullWidth onClick={onRetry} className="py-3.5" data-id="element-379" data-mp="1vnravp82ohh7">
              Try again
            </Button>
            <Button fullWidth variant="secondary" onClick={onExit} data-id="element-380" data-mp="1qt4xjgifkeuh">
              Not now
            </Button>
          </>}
        <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(declined ? 'Payment declined' : 'Payment issue')}`} className={`flex items-center justify-center gap-1.5 pt-2 text-xs font-semibold ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-381" data-mp="1gxa8o3nm5nj9">
          <Mail className="h-3.5 w-3.5" data-id="element-382" data-mp="37zmsfl8igj51" />
          Need help? {SUPPORT_EMAIL}
        </a>
      </div>
    </motion.div>;
}